import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useState, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useCreateSavedMessage } from '@features/savedMessages/hooks/useCreateSavedMessage';
import { useCreateEnterpriseSavedMessage } from '@features/savedMessages/hooks/useCreateEnterpriseSavedMessage';
import { authContext } from '@staffedupusa/shared-components';
import BundledEditor from '@utils/BundledEditor';
import { Close, HelpOutline } from '@mui/icons-material';
import { SavedMessagesIcon } from '../../../../vectors/index';
import { getStrippedText } from '@utils/helper';
import AssistantIcon from '@mui/icons-material/Assistant';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import { AITooltipText } from '@features/messaging';

type AddSavedMessageModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  initialMessage:
    | string
    | { label: string; content: string; id: number }
    | null;
  company_id?: number;
};

type FormData = {
  label: string;
  content: string;
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    label: yup
      .string()
      .required('Label is required')
      .test('not-empty-html', 'Label cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    content: yup
      .string()
      .required('Message is required')
      .test('not-empty-html', 'Message cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
  })
  .required();

const AddSavedMessageModal: FC<AddSavedMessageModalProps> = ({
  isOpen,
  handleClose,
  initialMessage,
  company_id,
}) => {
  const theme = useTheme();
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState('');
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      label: '',
      content:
        typeof initialMessage === 'string'
          ? initialMessage
          : typeof initialMessage === 'object' && initialMessage !== null
            ? initialMessage.content
            : '',
    },
  });

  const { createSavedMessage, isCreatingSavedMessage } = useCreateSavedMessage({
    setErrorText,
  });

  const { createEnterpriseSavedMessage, isCreatingEnterpriseSavedMessage } =
    useCreateEnterpriseSavedMessage({
      setErrorText,
    });

  const handleInsertAIContent = (content: string) => {
    setValue('content', content);
    setShowAiPromptModal(false);
  };

  const onSubmit = (values: FormData) => {
    if (authData?.user?.company) {
      createSavedMessage(values, {
        onSuccess: () => {
          handleClose();
          reset();
          setErrorText('');
        },
      });
    } else {
      createEnterpriseSavedMessage(
        {
          ...values,
          company_id,
          ...(initialMessage &&
          typeof initialMessage === 'object' &&
          initialMessage.label
            ? { saved_message_id: initialMessage.id }
            : {}),
        },
        {
          onSuccess: () => {
            handleClose();
            reset();
            setErrorText('');
          },
        },
      );
    }
  };
  useEffect(() => {
    if (isOpen && typeof initialMessage === 'string' && initialMessage) {
      setValue('content', initialMessage);
    }
    if (
      isOpen &&
      typeof initialMessage === 'object' &&
      initialMessage !== null
    ) {
      setValue('label', initialMessage.label);
      setValue('content', initialMessage.content);
    }
  }, [isOpen, initialMessage]);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          <SavedMessagesIcon />
          <br />
          <Typography variant="h4" mt={2}>
            Add Saved Message
          </Typography>
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            css={css`
              p {
                margin-left: ${theme.spacing(1)};
                font-size: 15px;
              }
            `}
          >
            <Stack sx={{ marginBottom: 4 }} spacing={3}>
              {errorText && <Alert severity="error">{errorText}</Alert>}
              <Typography>Label:</Typography>
              <Controller
                control={control}
                name="label"
                render={({ field }) => (
                  <TextField
                    {...field}
                    hiddenLabel
                    placeholder="Label"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.label)}
                    helperText={errors?.label?.message ?? ''}
                  />
                )}
              />
              <Typography>Message:</Typography>
              <Controller
                control={control}
                name="content"
                render={({ field }) => (
                  <Stack spacing={1}>
                    <BundledEditor
                      {...field}
                      onEditorChange={(newValue: string) =>
                        field.onChange(newValue)
                      }
                      onChange={() => {}}
                      init={{
                        height: 200,
                        menubar: false,
                        plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                        toolbar:
                          'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                        spellchecker_dialog: true,
                        spellchecker_rpc_url: 'localhost/ephox-spelling',
                        spellchecker_language: 'en_US',
                        spellchecker_active: true,
                        browser_spellcheck: true,
                        contextmenu: false,
                      }}
                    />
                    {Boolean(errors?.content?.message) && (
                      <Typography color="error">
                        {errors?.content?.message}
                      </Typography>
                    )}
                    <Stack mt={1} spacing={2} direction="row" alignItems="center" justifyContent='end'>
                      <Button
                        variant="outlined"
                        size='small'
                        onClick={() => setShowAiPromptModal(true)}
                        startIcon={<AssistantIcon />}
                      >
                        Write with AI
                      </Button>
                      <Tooltip title={AITooltipText} placement="top" arrow>
                        <HelpOutline color="secondary" fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Stack>
                )}
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                fullWidth
                disabled={
                  authData?.user?.company
                    ? isCreatingSavedMessage
                    : isCreatingEnterpriseSavedMessage
                }
                color="secondary"
                sx={{ marginTop: '15px !important' }}
                startIcon={<CheckIcon />}
              >
                Save
              </Button>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </>
  );
};

export default AddSavedMessageModal;
